import { berlinDay } from './berlin-day';
import { parseFirestoreDate } from './date-parser';
/**
 * SE Traceability: UC-003/UC-005/UC-007/UC-010; FR-022-018.
 * PO 2026-10-10: archived orphans remain searchable, outside operational booking inputs.
 */
import { BookingStatus } from '../types/domain-status';

/** Live records may carry non-string source/status values; those never match a known source or status. */
function upperText(value: unknown): string {
  return typeof value === 'string' ? value.toUpperCase() : '';
}

/** Operational visibility; other status rules (e.g. confirmed-only) remain consumer-specific. */
export type MergeMarkers = { status?: unknown; mergedInto?: unknown; botBooking?: { replacedByBookingId?: string; enteredInIlocaAt?: unknown } };
export function isMergedBooking(booking: MergeMarkers): boolean {
  return !!booking.mergedInto || !!booking.botBooking?.replacedByBookingId || upperText(booking.status) === 'MERGED_INTO_ILOCA';
}
export function isActiveBooking(booking: MergeMarkers): boolean {
  return !isMergedBooking(booking) && upperText(booking.status) !== BookingStatus.ARCHIVED_ORPHAN;
}

/** PO 2026-10-07: cleaning originates exclusively from iLoca. */
export function createsCleaningTasks(booking: MergeMarkers & { source?: string; metadata?: { illocaId?: unknown; sources?: string[] } }): boolean {
  return !isMergedBooking(booking) && (upperText(booking.source) === 'ILLOCA'
    || booking.metadata?.illocaId != null
    || (Array.isArray(booking.metadata?.sources) && booking.metadata.sources.some(source => upperText(source) === 'ILLOCA')));
}

/** Operational cleaning eligibility, without changing the sync's provenance check. */
export function isActiveCleaningBooking(booking: Parameters<typeof createsCleaningTasks>[0] & { status?: unknown }): boolean {
  return isActiveBooking(booking) && createsCleaningTasks(booking);
}

export function isBotBooking(booking: { source?: string; metadata?: { illocaId?: unknown } }): boolean {
  return booking.metadata?.illocaId == null && ['VOICE', 'WHATSAPP'].includes(upperText(booking.source));
}

export const ACTIVE_BOT_BOOKING_STATUSES = ['CONFIRMED', 'CHANGED', 'MODIFIED', 'PENDING', 'CHECKED_IN'];
export function isActiveBotBooking(booking: { source?: string; status?: string; botBooking?: { enteredInIlocaAt?: unknown } }): boolean {
  return isBotBooking(booking) && !isBotBookingClosed(booking)
    && ACTIVE_BOT_BOOKING_STATUSES.includes(upperText(booking.status));
}

/** PO 17.10.2026: booking requests end on departure or manual completion; age alone is irrelevant. */
export function isOpenBotBookingRequest(booking: Parameters<typeof isActiveBotBooking>[0] & {
  metadata?: { illocaId?: unknown }; stay?: { checkOut?: unknown }; botBooking?: { enteredInIlocaAt?: unknown; replacedByBookingId?: string };
}, now: Date): boolean {
  const checkOut = parseFirestoreDate(booking.stay?.checkOut);
  return isActiveBotBooking(booking) && !booking.botBooking?.replacedByBookingId
    && !!checkOut && berlinDay(checkOut) >= berlinDay(now);
}

/** PO 2026-10-07: manual iLoca entry closes the Firestore-only bot record. */
export function isBotBookingClosed(booking: MergeMarkers): boolean {
  return isMergedBooking(booking) || booking.botBooking?.enteredInIlocaAt != null;
}

/** Shared candidate rule for booking lookup and matching. */
export function isBookingLookupCandidate(booking: { status?: string; botBooking?: { enteredInIlocaAt?: unknown } }): boolean {
  return booking.status !== 'CANCELLED' && !isBotBookingClosed(booking);
}

/** Cancellation is a booking status; open guest items retain their own lifecycle. */
export function isBookingCancelled(booking: { status?: unknown; bookingStatus?: unknown; cancelled?: unknown; metadata?: { wasCancelled?: boolean; [key: string]: unknown } }): boolean {
  return String(booking.bookingStatus ?? booking.status ?? '').toUpperCase() === 'CANCELLED'
    || booking.cancelled === true || booking.metadata?.wasCancelled === true;
}

/** Stay-bound work excludes cancellations, blocks, archives, no-shows, checked-out and closed/replaced bots. */
export function isStayBookingActive(booking: { status?: unknown; bookingStatus?: unknown; cancelled?: unknown; blocked?: boolean; metadata?: { wasCancelled?: boolean; [key: string]: unknown }; botBooking?: { enteredInIlocaAt?: unknown; replacedByBookingId?: string } }): boolean {
  const status = String(booking.bookingStatus ?? booking.status ?? '').toUpperCase();
  return isActiveBooking({ status }) && !isBookingCancelled(booking) && !isBotBookingClosed(booking)
    && !['BLOCKED', 'ARCHIVED', 'NO_SHOW', 'CHECKED_OUT'].includes(status)
    && booking.blocked !== true && !booking.botBooking?.replacedByBookingId;
}

/** #518: tasks belong to a reference shared by all owners; retire it only without a live owner. */
export function retiredBookingReferences(bookings: Array<MergeMarkers & { bookingReference: string }>,
  candidates = bookings.filter(b => isMergedBooking(b) || isBookingCancelled(b)).map(b => b.bookingReference)): string[] {
  const live = new Set(bookings.filter(b => isActiveBooking(b) && !isBookingCancelled(b)).map(b => b.bookingReference));
  return [...new Set(candidates)].filter(ref => !live.has(ref));
}
